import { Box, Group, Stack, Text } from "@mantine/core";
import { ColorDot, ImageZoom } from "../ui";
import type { ModelPresenter } from "./presenter";

interface ModelThumbsProps<TModel extends { id: string; url: string }> {
  models: TModel[];
  presenter: ModelPresenter<TModel>;
  /** Shown first, as the source of all the models. */
  original?: { url: string };
  /** When given, thumbnails become a picker (highlighted selection, click to select). */
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  size?: number;
}

/** A horizontal strip of model thumbnails: the generate result and the 3D-viewer picker. */
export default function ModelThumbs<TModel extends { id: string; url: string }>({
  models,
  presenter,
  original,
  selectedId,
  onSelect,
  size = 120,
}: ModelThumbsProps<TModel>) {
  const selectable = onSelect !== undefined;

  return (
    <Box style={{ overflowX: "auto", paddingBottom: 4 }}>
      <Group gap="sm" align="flex-start" wrap={selectable ? "nowrap" : "wrap"}>
        {original && (
          <Stack gap={4} align="center">
            <Text size="xs" c="dimmed">
              Original
            </Text>
            <ImageZoom src={original.url} alt="original" w={size} h={size} />
          </Stack>
        )}
        {models.map((model) => {
          const { src, tint, label } = presenter.thumb(model);
          const isSelected = selectedId === model.id;
          return (
            <Stack
              key={model.id}
              gap={4}
              align="center"
              onClick={selectable ? () => onSelect(model.id) : undefined}
              style={{
                cursor: selectable ? "pointer" : undefined,
                flexShrink: 0,
                borderRadius: 10,
                padding: 4,
                border: isSelected ? "2px solid var(--mantine-color-brand-5)" : "2px solid transparent",
              }}
            >
              <Group gap={4} wrap="nowrap">
                <ColorDot color={tint} />
                <Text size="xs" c={isSelected ? "brand" : "dimmed"} fw={isSelected ? 600 : 400} truncate maw={size}>
                  {label}
                </Text>
              </Group>
              {src ? (
                <ImageZoom src={src} alt={label} w={size} h={size} background={tint} zoom={!selectable} />
              ) : (
                <Box w={size} h={size} style={{ borderRadius: 8, background: tint }} />
              )}
            </Stack>
          );
        })}
      </Group>
    </Box>
  );
}
